import { ExpressionValue } from "../../../rule-engine-core/Expressions/ExpressionValue";
import { ExpressionValueType } from "../../../rule-engine-core/Expressions/ExpressionValueType";
import { ExpressionRule } from "../../../rule-engine-core/Expressions/ExpressionRule";
import { EqualExpression } from "../../../rule-engine-core/Expressions/Expressions/Math/EqualExpression";
import { NotEqualExpression } from "../../../rule-engine-core/Expressions/Expressions/Math/NotEqualExpression";
import { AndGroup, GroupingType } from "../../../rule-engine-core/Expressions/Expressions/Logic/AndGroup";
import { ExistsExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/Exists";
import { GreaterThanExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/GreaterThan";
import { GreaterThanEqualToExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/GreaterThanEqualTo";
import {
    IfThenExpression,
    IfThenPair,
} from "../../../rule-engine-core/Expressions/Expressions/Logic/IfThenExpression";
import { IsFalseExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/IsFalse";
import { IsTrueExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/IsTrue";
import { LessThanExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/LessThanExpression";
import { LessThanEqualToExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/LessThanEqualToExpression";
import { NotExpression } from "../../../rule-engine-core/Expressions/Expressions/Logic/NotExpression";
import { StringContainsExpression } from "../../../rule-engine-core/Expressions/Expressions/String/StringContainsExpression";
import { StringEqualExpression } from "../../../rule-engine-core/Expressions/Expressions/String/StringEqualExpression";
import { StringLengthExpression } from "../../../rule-engine-core/Expressions/Expressions/String/StringLengthExpression";
import { StringListInExpression } from "../../../rule-engine-core/Expressions/Expressions/String/StringListInExpression";
import { StringStartWithExpression } from "../../../rule-engine-core/Expressions/Expressions/String/StringStartWithExpression";
import type {
    RuleSummaryAst,
    RuleSummaryExpression,
    RuleSummaryOperand,
    RuleSummaryValue,
} from "../domain/ruleSummary";

export type AppGlueExpressionSummary = {
    expression: RuleSummaryExpression;
    covered: boolean;
    warnings: string[];
};

export type AppGlueIfThenSummary = {
    ast: RuleSummaryAst;
    covered: boolean;
    warnings: string[];
};

const expressionLabel = (expressionRule: ExpressionRule | undefined): string =>
    expressionRule?.__type ?? "blank expression value";

const unsupportedExpression = (label: string): AppGlueExpressionSummary => ({
    expression: {
        kind: "unsupported",
        label,
    },
    covered: false,
    warnings: [`Unsupported AppGlue expression in summary: ${label}.`],
});

const summarizeStringLengthOperand = (
    expressionRule: StringLengthExpression,
): { operand: RuleSummaryOperand; covered: boolean; warnings: string[] } => {
    const stringValue = summarizeOperand(expressionRule.value1);

    return {
        operand: {
            kind: "function",
            name: "length",
            args: [stringValue.operand],
        },
        covered: stringValue.covered,
        warnings: stringValue.warnings,
    };
};

const summarizeOperand = (value: ExpressionValue): { operand: RuleSummaryOperand; covered: boolean; warnings: string[] } => {
    if (value.valueType === ExpressionValueType.VARIABLE && value.variableName) {
        return {
            operand: { kind: "field", field: value.variableName },
            covered: true,
            warnings: [],
        };
    }

    if (value.valueType === ExpressionValueType.VALUE) {
        return {
            operand: { kind: "literal", value: value.value as RuleSummaryValue },
            covered: true,
            warnings: [],
        };
    }

    if (value.valueType === ExpressionValueType.SUBEXPRESSION && value.subExpression instanceof StringLengthExpression) {
        return summarizeStringLengthOperand(value.subExpression);
    }

    const label =
        value.valueType === ExpressionValueType.SUBEXPRESSION
            ? expressionLabel(value.subExpression)
            : "blank expression value";

    return {
        operand: { kind: "native_expression", label },
        covered: false,
        warnings: [`Unsupported AppGlue expression operand in summary: ${label}.`],
    };
};

const summarizeBooleanPredicate = (
    expressionRule: IsTrueExpression | IsFalseExpression,
    expected: boolean,
): AppGlueExpressionSummary => {
    const left = summarizeOperand(expressionRule.value1);

    return {
        expression: {
            kind: "comparison",
            left: left.operand,
            operator: "==",
            right: { kind: "literal", value: expected },
        },
        covered: left.covered,
        warnings: left.warnings,
    };
};

const summarizeBinaryExpression = (
    expressionRule:
        | StringEqualExpression
        | EqualExpression
        | NotEqualExpression
        | GreaterThanExpression
        | GreaterThanEqualToExpression
        | LessThanExpression
        | LessThanEqualToExpression,
    operator: "==" | "!=" | ">" | ">=" | "<" | "<=",
): AppGlueExpressionSummary => {
    const left = summarizeOperand(expressionRule.value1);
    const right = summarizeOperand(expressionRule.value2);

    return {
        expression: {
            kind: "comparison",
            left: left.operand,
            operator,
            right: right.operand,
        },
        covered: left.covered && right.covered,
        warnings: [...left.warnings, ...right.warnings],
    };
};

const summarizeStringPredicateExpression = (
    expressionRule: StringContainsExpression | StringStartWithExpression,
    kind: "contains" | "starts_with",
): AppGlueExpressionSummary => {
    const left = summarizeOperand(expressionRule.value1);
    const right = summarizeOperand(expressionRule.value2);

    return {
        expression: {
            kind,
            left: left.operand,
            right: right.operand,
        },
        covered: left.covered && right.covered,
        warnings: [...left.warnings, ...right.warnings],
    };
};

const summarizeStringListInExpression = (expressionRule: StringListInExpression): AppGlueExpressionSummary => {
    const left = summarizeOperand(expressionRule.value1);
    const right = summarizeOperand(expressionRule.value2);

    if (right.operand.kind !== "literal" || !Array.isArray(right.operand.value)) {
        return {
            expression: {
                kind: "unsupported",
                label: "String List In with non-literal list",
            },
            covered: false,
            warnings: ["String List In summary requires a literal list operand."],
        };
    }

    return {
        expression: {
            kind: "in_list",
            left: left.operand,
            values: right.operand.value,
        },
        covered: left.covered,
        warnings: left.warnings,
    };
};

const summarizeAndGroup = (expressionRule: AndGroup): AppGlueExpressionSummary => {
    if (expressionRule.typeOfGroup !== GroupingType.AND) {
        return unsupportedExpression(expressionLabel(expressionRule));
    }

    const expressionSummaries = expressionRule.elements.map((element) =>
        element.subExpression
            ? summarizeAppGlueExpressionRule(element.subExpression)
            : unsupportedExpression("blank expression value"),
    );
    const warnings = expressionSummaries.flatMap((summary) => summary.warnings);
    const covered = expressionSummaries.every((summary) => summary.covered);
    const expressions = expressionSummaries.map((summary) => summary.expression);

    return {
        expression: expressions.length === 1 ? expressions[0]! : { kind: "and", expressions },
        covered,
        warnings,
    };
};

const summarizeNotExpression = (expressionRule: NotExpression): AppGlueExpressionSummary => {
    if (!expressionRule.value1.subExpression) return unsupportedExpression("Not with blank operand");

    const inner = summarizeAppGlueExpressionRule(expressionRule.value1.subExpression);
    return {
        expression: {
            kind: "not",
            expression: inner.expression,
        },
        covered: inner.covered,
        warnings: inner.warnings,
    };
};

export const summarizeAppGlueExpressionRule = (expressionRule: ExpressionRule): AppGlueExpressionSummary => {
    if (expressionRule instanceof StringEqualExpression) return summarizeBinaryExpression(expressionRule, "==");
    if (expressionRule instanceof EqualExpression) return summarizeBinaryExpression(expressionRule, "==");
    if (expressionRule instanceof NotEqualExpression) return summarizeBinaryExpression(expressionRule, "!=");
    if (expressionRule instanceof StringListInExpression) return summarizeStringListInExpression(expressionRule);
    if (expressionRule instanceof IsTrueExpression) return summarizeBooleanPredicate(expressionRule, true);
    if (expressionRule instanceof IsFalseExpression) return summarizeBooleanPredicate(expressionRule, false);
    if (expressionRule instanceof ExistsExpression) {
        return expressionRule.dataPath
            ? {
                  expression: { kind: "exists", field: expressionRule.dataPath },
                  covered: true,
                  warnings: [],
              }
            : unsupportedExpression("Exists with blank data path");
    }
    if (expressionRule instanceof GreaterThanExpression) return summarizeBinaryExpression(expressionRule, ">");
    if (expressionRule instanceof GreaterThanEqualToExpression) return summarizeBinaryExpression(expressionRule, ">=");
    if (expressionRule instanceof LessThanExpression) return summarizeBinaryExpression(expressionRule, "<");
    if (expressionRule instanceof LessThanEqualToExpression) return summarizeBinaryExpression(expressionRule, "<=");
    if (expressionRule instanceof StringContainsExpression) {
        return summarizeStringPredicateExpression(expressionRule, "contains");
    }
    if (expressionRule instanceof StringStartWithExpression) {
        return summarizeStringPredicateExpression(expressionRule, "starts_with");
    }
    if (expressionRule instanceof NotExpression) return summarizeNotExpression(expressionRule);
    if (expressionRule instanceof AndGroup) return summarizeAndGroup(expressionRule);

    return unsupportedExpression(expressionLabel(expressionRule));
};

const summarizeBranch = (branch: IfThenPair, index: number) => {
    const conditionSummary = summarizeAppGlueExpressionRule(branch.ifExpression);
    return {
        statement: {
            kind: "condition" as const,
            keyword: index === 0 ? ("IF" as const) : ("ELSE IF" as const),
            expression: conditionSummary.expression,
            result: expressionValueToSummaryValue(branch.thenExpression),
        },
        covered: conditionSummary.covered,
        warnings: conditionSummary.warnings,
    };
};

export const expressionValueToSummaryValue = (value: ExpressionValue): RuleSummaryValue => {
    if (value.valueType === ExpressionValueType.VALUE) return value.value as RuleSummaryValue;
    if (value.valueType === ExpressionValueType.VARIABLE) return value.variableName;
    if (value.valueType === ExpressionValueType.SUBEXPRESSION) return expressionLabel(value.subExpression);
    return undefined;
};

export const summarizeIfThenExpression = (expression: IfThenExpression): AppGlueIfThenSummary => {
    const branchSummaries = expression.ifBranches.map(summarizeBranch);
    const warnings = branchSummaries.flatMap((summary) => summary.warnings);

    return {
        ast: {
            kind: "rule_summary",
            statements: [
                ...branchSummaries.map((summary) => summary.statement),
                {
                    kind: "else",
                    result: expressionValueToSummaryValue(expression.defaultOutput),
                },
            ],
        },
        covered: branchSummaries.every((summary) => summary.covered),
        warnings,
    };
};
